import { motion } from "framer-motion";
import { IMAGES } from "@/lib/content";
import { useLang } from "@/lib/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Motion";
import LeafDecor from "@/components/LeafDecor";

export default function ProductLines() {
  const { c } = useLang();
  const p = c.products;
  return (
    <section id="products" data-testid="products-section" className="relative py-28 sm:py-40 bg-[#DCD6C1] border-y border-forest/10 overflow-hidden">
      <LeafDecor layers={[
        { img: "bunch", className: "bottom-0 left-0 w-[360px] sm:w-[460px] -ml-16 -mb-14 -rotate-6", opacity: 0.38 },
        { img: "corner", className: "top-0 right-0 w-[420px] -mt-16 -mr-20 rotate-[120deg]", opacity: 0.3 },
        { img: "side", className: "top-1/2 left-1/2 -translate-x-1/2 h-[320px] rotate-[8deg] hidden xl:block", opacity: 0.14 },
      ]} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <Eyebrow testid="products-eyebrow">{p.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif font-light leading-none tracking-[-0.02em] text-forest text-5xl sm:text-6xl lg:text-7xl">
              {p.heading.pre}<span className="text-gold italic">{p.heading.em}</span>
            </h2>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm font-sans text-sm leading-relaxed text-forest/95">{p.subcopy}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
          {p.items.map((item, i) => (
            <Reveal key={item.id} delay={i * 0.12}>
              <motion.article data-testid={`product-${item.id}`} whileHover="hover" className="group relative border border-forest/10 bg-white overflow-hidden h-full shadow-[0_24px_60px_-30px_rgba(3,22,12,0.35)]">
                <div className="relative h-[320px] sm:h-[380px] overflow-hidden">
                  <motion.img variants={{ hover: { scale: 1.06 } }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} src={IMAGES[item.image]} alt={item.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
                  <span className={`absolute top-5 left-5 font-sans text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border bg-white/80 backdrop-blur-sm ${item.accent === "gold" ? "border-gold text-gold" : "border-leaf text-leaf"}`}>
                    {item.badge}
                  </span>
                </div>

                <div className="p-7 sm:p-9">
                  <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-forest/85">{p.originLabel}{item.origin}</div>
                  <h3 className="mt-3 font-serif text-3xl sm:text-4xl text-forest">{item.name}</h3>
                  <p className="mt-4 font-sans text-sm leading-relaxed text-forest/95">{item.positioning}</p>
                  <div className="mt-5 pt-5 border-t border-forest/10">
                    <p className="font-sans text-xs leading-relaxed text-forest/90">
                      <span className="text-gold">{p.customerLabel}</span>{item.customer}
                    </p>
                  </div>
                  <blockquote className="mt-6 font-serif italic text-xl sm:text-2xl text-gold">“{item.quote}”</blockquote>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
